import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { X, ArrowRight, ShieldCheck } from "lucide-react";
export default function Servicedomainscapabilities() {
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const services = [{
    category: "GOVERNANCE & COMPLIANCE",
    title: "Cybersecurity Governance, Risk & Compliance",
    description: "Establish practical security governance and compliance structures aligned with organizational priorities and applicable requirements.",
    points: "Cybersecurity governance frameworks, Risk assessment and treatment planning, ISO/IEC 27001 readiness advisory, UAE regulatory alignment"
  }, {
    category: "SECURITY ASSURANCE",
    title: "Security Assessments & Validation",
    description: "Identify security gaps and validate control effectiveness through focused assessment and assurance activities.",
    points: "Security architecture reviews, Vulnerability assessment advisory, Penetration testing coordination, Remediation and control validation"
  }, {
    category: "SECURITY OPERATIONS",
    title: "Security Operations & Threat Management",
    description: "Strengthen operational security capabilities through advisory, readiness and threat-informed improvement.",
    points: "SOC advisory and operating models, Threat intelligence governance, Incident response readiness, Detection use-case improvement"
  }, {
    category: "SECURE INFRASTRUCTURE",
    title: "Infrastructure, Network & Cloud Security",
    description: "Design secure and resilient infrastructure foundations that support modern enterprise operations.",
    points: "Secure network architecture, Segmentation and access control, Firewall and security platform integration, Cloud and hybrid security architecture"
  }, {
    category: "APPLICATION & API SECURITY",
    title: "API Security & Application Protection",
    description: "Protect digital services and connected applications through secure architecture and layered protection controls.",
    points: "API security architecture, API management and gateway integration, WAF and application protection, Microservices and DevSecOps guidance"
  }, {
    category: "SYSTEM INTEGRATION",
    title: "Enterprise Integration & Digital Platforms",
    description: "Connect systems, applications and workflows through secure, practical integration capabilities.",
    points: "Enterprise application integration, Middleware and API-led connectivity, Workflow automation enablement, Secure platform implementation support"
  }, {
    category: "EDUCATION TECHNOLOGY",
    title: "Academic Scheduling & Student Systems",
    description: "Enable universities and education institutions with specialized platforms supporting academic planning and operations.",
    points: "Academic scheduling system solutions, Student Information System (SIS) integration, Learning Management System (LMS) connectivity, Academic operations enablement"
  }, {
    category: "GOVERNMENT & ENTERPRISE",
    title: "Secure Digital Enablement",
    description: "Support government and enterprise organizations with coordinated secure technology and modernization initiatives.",
    points: "Digital service enablement, Enterprise technology modernization, Government and regulated environment support, Secure delivery planning"
  }];
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsExpanded(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  const handleCardClick = (index: number) => {
    setSelectedCardId(index);
    setIsExpanded(true);
  };
  const selectedService = selectedCardId !== null ? services[selectedCardId] : null;
  return <section data-section-id="256148" id="service-domains" className="bg-[#0B1220] text-white pt-12 md:pt-16 pb-20 md:pb-24">
      <div className="container mx-auto px-6 md:px-12 max-w-[1280px]">
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <Label className="font-mono text-[#0F62FE] tracking-[0.2em] uppercase mb-4 block text-sm">
            CAPABILITY DOMAINS
          </Label>
          <h2 className="font-default text-4xl md:text-5xl font-bold mb-6 tracking-tight text-white">
            Select a Capability Area to View Details
          </h2>
          <p className="font-default text-lg text-[#D6DCE8] leading-relaxed">
            Each domain combines advisory, architecture and implementation-focused support. Select a card to explore the detailed capabilities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {services.map((service, i) => <Card key={i} data-index={i} onClick={() => handleCardClick(i)} className="bg-[#142033] border-white/10 hover:border-[#0F62FE]/50 transition-all duration-500 group cursor-pointer h-full flex flex-col rounded-lg shadow-md p-8 hover:translate-y-[-4px]">
              <CardHeader className="p-0 mb-4">
                <span className="font-mono text-[11px] md:text-[12px] tracking-wider text-[#0F62FE] mb-3 block uppercase font-bold">
                  {service.category}
                </span>
                <CardTitle className="font-default text-[19px] md:text-[22px] font-bold tracking-tight text-white group-hover:text-[#0F62FE] transition-colors duration-300 leading-tight">
                  {service.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 flex flex-col gap-4 flex-grow">
                <p className="font-default text-[14px] md:text-[15px] text-[#D6DCE8] leading-relaxed line-clamp-3">
                  {service.description}
                </p>
                <div className="mt-auto pt-4 flex items-center gap-2 text-[#0F62FE] font-medium text-sm group-hover:gap-3 transition-all duration-300">
                  View Details <ArrowRight className="w-4 h-4" />
                </div>
              </CardContent>
            </Card>)}
        </div>

        {}
        <Dialog open={isExpanded && !!selectedService} onOpenChange={setIsExpanded}>
          <DialogContent className="hidden md:block z-[100] bg-[#17263B] border-[#35506E] text-white max-w-2xl p-0 overflow-hidden rounded-lg shadow-2xl animate-in fade-in zoom-in duration-300 [&>button]:text-white [&>button]:hover:text-[#00D4FF] [&>button]:cursor-pointer [&>button]:transition-colors [&>button]:text-white [&>button]:opacity-100 [&>button]:transition-colors [&>button]:duration-200 [&>button:hover]:text-[#0F62FE] [&_button]:cursor-pointer">
            <div className="relative p-8 md:p-12">
              
              <div className="mb-8">
                <span className="font-mono text-xs tracking-widest text-[#00D4FF] mb-4 block uppercase font-bold">
                  {selectedService?.category}
                </span>
                <DialogTitle className="font-default text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
                  {selectedService?.title}
                </DialogTitle>
                <DialogDescription className="font-default text-lg text-white/90 leading-relaxed mb-8">
                  {selectedService?.description}
                </DialogDescription>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-mono text-white/70 font-semibold uppercase tracking-widest mb-4">Key Capabilities</h4>
                <div className="grid grid-cols-1 gap-4">
                  {selectedService?.points.split(', ').map((point, idx) => <div key={idx} className="flex items-start gap-4 p-4 bg-[#1E2F4D] rounded-lg border border-[#35506E]">
                      <ShieldCheck className="w-5 h-5 text-[#0F62FE] shrink-0 mt-0.5" />
                      <span className="font-default text-[15px] text-white font-medium leading-snug">
                        {point}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {}
        <Dialog open={isExpanded && !!selectedService} onOpenChange={setIsExpanded}>
          <DialogContent className="md:hidden z-[100] bg-[#17263B] border-t border-[#35506E] text-white w-full max-w-none h-[80vh] bottom-0 top-auto translate-y-0 p-0 rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom duration-500 [&>button]:text-white [&>button]:hover:text-[#00D4FF] [&>button]:cursor-pointer [&>button]:transition-colors">
            <div className="p-8 overflow-y-auto h-full">
              <div className="w-12 h-1.5 bg-white/10 rounded-lg mx-auto mb-8" />

              <span className="font-mono text-[10px] tracking-widest text-[#00D4FF] mb-3 block uppercase font-bold">
                {selectedService?.category}
              </span>
              <DialogTitle className="font-default text-2xl font-bold tracking-tight text-white mb-4">
                {selectedService?.title}
              </DialogTitle>
              <p className="font-default text-[15px] text-white/90 leading-relaxed mb-8">
                {selectedService?.description}
              </p>

              <Accordion type="single" collapsible className="w-full border-t border-[#35506E]">
                <AccordionItem value="details" className="border-b border-[#35506E]">
                  <AccordionTrigger className="text-white font-mono text-xs uppercase tracking-widest py-4 hover:no-underline">
                    Detailed Capabilities
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-4 pb-6">
                      {selectedService?.points.split(', ').map((point, idx) => <li key={idx} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-lg bg-[#0F62FE] mt-2 shrink-0" />
                          <span className="text-[14px] text-white font-medium">{point}</span>
                        </li>)}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>;
}